/**
 * Domínio de AÇÕES de um plano (GUPM-213) — TS puro, sem React/Supabase/DOM.
 *
 * Reúne o rascunho de ação (`AcaoDraft`) e seus helpers, compartilhados entre o
 * "Novo Plano de Ação" (MPA, GUPM-193) e o editor de ações embutido na BAIXA da
 * TARP (GUPM-213). O modelo migrou de `features/planos/planoForm.ts` para cá para
 * poder ser reusado por duas features sem que uma importe da outra (architecture.md).
 *
 * Fiel ao `closeTarpFinal` do V8: ao definir um plano na baixa, exige ≥1 ação com
 * descrição; o resumo textual (`resumoPlano`) alimenta `tarp_records.plano_acao`.
 *
 * ⚠️ Fase red (GUPM-213): os helpers NOVOS lançam NOT_IMPLEMENTED de propósito — os
 * testes devem falhar por COMPORTAMENTO. A implementação vem na fase green.
 */
import { PAPEIS_EXECUCAO, SETORES } from './mpa';
import type { AcaoPlano } from './index';

/** Sentinela do responsável fora dos papéis curados → habilita o campo de texto livre. */
export const OUTROS_RESP = 'Outros';

/** Estado de edição de UMA ação (antes de virar `AcaoPlano` no submit). */
export interface AcaoDraft {
  id: string;
  descricao: string;
  /** Papel curado (PAPEIS_EXECUCAO) OU `OUTROS_RESP` (então vale `responsavelOutro`). */
  responsavel: string;
  responsavelOutro: string;
  setor: string;
  /** '' ou yyyy-mm-dd — a data máxima de conclusão desta ação. */
  conclusao: string;
}

/** Rascunho de ação em branco (responsável/setor no 1º item curado). */
export function novaAcaoDraft(id: string): AcaoDraft {
  return {
    id,
    descricao: '',
    responsavel: PAPEIS_EXECUCAO[0] ?? '',
    responsavelOutro: '',
    setor: SETORES[0] ?? '',
    conclusao: '',
  };
}

/** Responsável efetivo: o papel curado ou, se "Outros", o texto livre (trim). */
export function responsavelDaAcao(a: AcaoDraft): string {
  return a.responsavel === OUTROS_RESP ? a.responsavelOutro.trim() : a.responsavel;
}

/**
 * A ação tem algo além do rascunho em branco (`novaAcaoDraft`) — C-91: fechar o
 * formulário com uma ação mexida pergunta antes de descartar; a ação em branco que
 * o formulário já abre não conta.
 */
export function acaoPreenchida(a: AcaoDraft): boolean {
  const branca = novaAcaoDraft(a.id);
  return (
    a.descricao.trim() !== '' ||
    a.responsavelOutro.trim() !== '' ||
    a.conclusao !== '' ||
    a.responsavel !== branca.responsavel ||
    a.setor !== branca.setor
  );
}

// ── Helpers de manipulação/derivação (GUPM-213) ─────────────────────────────

/** Acrescenta uma ação em branco (com `id`) ao fim — imutável. */
export function addAcao(acoes: AcaoDraft[], id: string): AcaoDraft[] {
  return [...acoes, novaAcaoDraft(id)];
}

/** Remove a ação de `id` — imutável. */
export function removeAcao(acoes: AcaoDraft[], id: string): AcaoDraft[] {
  return acoes.filter((a) => a.id !== id);
}

/** Edita campos da ação de `id` — imutável. */
export function patchAcao(acoes: AcaoDraft[], id: string, patch: Partial<AcaoDraft>): AcaoDraft[] {
  return acoes.map((a) => (a.id === id ? { ...a, ...patch } : a));
}

/**
 * Uma ação é válida quando tem descrição (trim), responsável efetivo e **data máxima
 * de conclusão** não-vazios.
 *
 * GUPM-295: o prazo passou a ser obrigatório. Plano de ação sem prazo não é
 * acompanhável — não há como dizer se a ação está em dia, atrasada ou esquecida, e o
 * acompanhamento é o que dá sentido ao plano no PSB. O campo já existia na tela
 * (`AcaoCard`, "Data máxima de conclusão"); o que faltava era exigi-lo.
 */
export function acaoValida(a: AcaoDraft): boolean {
  return (
    a.descricao.trim().length > 0 &&
    responsavelDaAcao(a).length > 0 &&
    a.conclusao.trim().length > 0
  );
}

/**
 * A ação foi COMEÇADA pelo RT quando tem descrição. `responsavel` e `setor` nascem com
 * default (`PAPEIS_EXECUCAO[0]` / `SETORES[0]`) e só `descricao` e `conclusao` nascem
 * vazias — então a descrição é o único sinal confiável de intenção. Linha totalmente em
 * branco não é ação começada e segue sendo ignorada.
 */
function acaoComecada(a: AcaoDraft): boolean {
  return a.descricao.trim().length > 0;
}

/** Campo que pode faltar numa ação já começada. */
export type FaltaNaAcao = 'prazo' | 'responsavel';

/**
 * TUDO que falta para a ação começada valer — vazio quando está completa ou nem começou.
 *
 * GUPM-295 (adendo): existe para que o bloqueio NOMEIE o campo ausente. Sem isto, uma
 * ação com descrição e sem prazo era descartada por `acoesValidas` e a baixa caía em
 * "Adicione ao menos 1 ação" — falso, já que o RT vê a ação na tela. É a mesma classe
 * de defeito que o GUPM-294 consertou; não vale reintroduzi-la por outra porta.
 *
 * Devolve LISTA, não um campo priorizado (self-review do #180): com prazo e responsável
 * faltando na mesma ação, reportar só um faria o RT corrigir, reenviar e só então
 * descobrir o outro — o mesmo defeito pela metade.
 */
export function faltasNaAcao(a: AcaoDraft): FaltaNaAcao[] {
  if (!acaoComecada(a) || acaoValida(a)) return [];
  const faltas: FaltaNaAcao[] = [];
  if (responsavelDaAcao(a).length === 0) faltas.push('responsavel');
  if (a.conclusao.trim().length === 0) faltas.push('prazo');
  return faltas;
}

/** Só as ações válidas (descartando linhas em branco). */
export function acoesValidas(acoes: AcaoDraft[]): AcaoDraft[] {
  return acoes.filter(acaoValida);
}

/** Converte um rascunho em `AcaoPlano` (destino persistido). `conclusao` → `prazo`. */
export function draftToAcaoPlano(a: AcaoDraft): AcaoPlano {
  return {
    id: a.id,
    descricao: a.descricao.trim(),
    responsavel: responsavelDaAcao(a),
    setor: a.setor || null,
    prazo: a.conclusao || null,
    status: 'pendente',
  };
}

/**
 * Resumo textual do plano para `tarp_records.plano_acao` (auditoria/compatibilidade):
 * `PA-00042 · 3 ações · 1ª: <descrição>` (com código) ou `3 ações · 1ª: <descrição>`
 * (sem código, quando ainda não foi gerado). Conta só ações válidas; '' se não houver.
 */
export function resumoPlano(acoes: AcaoDraft[], codigo?: string): string {
  const validas = acoesValidas(acoes);
  if (validas.length === 0) return '';
  const n = validas.length;
  const primeira = (validas[0] as AcaoDraft).descricao.trim();
  const base = `${n} ${n === 1 ? 'ação' : 'ações'} · 1ª: ${primeira}`;
  return codigo ? `${codigo} · ${base}` : base;
}

/**
 * Resumo textual ao VINCULAR um plano JÁ existente na baixa (Extra 2), para
 * `tarp_records.plano_acao`: `PA-00007 · 2 ações (plano existente)`. `undefined` — plano
 * fora do cache/lista (o `find` não achou) — cai num fallback NÃO-vazio, para nunca gravar
 * `plano_acao` vazio (review #124). Puro/testável — a UI não formata isto inline.
 */
export function resumoPlanoExistente(
  plano: { codigo: string; acoes: readonly unknown[] } | undefined,
): string {
  if (!plano) return 'Plano de ação vinculado';
  const n = plano.acoes.length;
  return `${plano.codigo} · ${n} ${n === 1 ? 'ação' : 'ações'} (plano existente)`;
}
